/**
 * The puja catalogue — every word a devotee reads about a ritual, as data.
 *
 * **The significance field is the sharpest edge in this console: no outcome claim, ever.** Not
 * health, wealth, jobs, court cases, marriage, fertility or "removes dosh". The helper text says
 * so, and the server's content lint runs on every write and refuses with the rule it broke. This
 * screen does **not** re-check the text before sending: a second copy of that rule would drift
 * from the one the deploy actually runs, and the drift would be invisible until something shipped.
 *
 * **A puja name is never translated.** The transliteration and the Devanagari are two fields, and
 * the label on the first says why.
 *
 * **A published slug is frozen.** Changing it breaks every inbound link and search result already
 * pointing at the page, so the server refuses — and the field is read-only here rather than
 * accepting text it will reject.
 *
 * **A tier is made here and priced on the pricing screen.** Creating one, describing what it
 * includes and retiring it are catalogue decisions; what it costs is a price version. The two
 * screens read the same tier list, so the split never shows two answers.
 */
import { useEffect, useState } from 'react';
import { Chip, Kicker, Note, StatusChip } from '../components/primitives';
import { Input, Select, Toggle } from '../components/forms';
import { DataTable, DetailPage, OrderedListEditor, type OrderedRow } from '../components/data';
import { KV } from '../components/entities';
import type { components } from '../api/generated/schema';
import { ConfirmAction, FilterBar, ScopeAction, Section } from '../components/kit';
import { EmptyState, ErrorState, LoadingState } from '../components/feedback';
import { can, type Scope } from '../lib/rbac';
import { QUEUE_HINT, useQueue } from '../lib/useQueue';
import { count } from '../lib/text';
import { useConsole } from '../app/console';
import { useUrlTab } from '../lib/filterCodecs';
import { NewPujaForm } from './CatalogueCreate';
import { PrepChecklistEditor, ProvisionEditor } from './PujaPreparation';
import { errorMessage } from '../lib/api/client';
import { useMediaLibrary } from '../lib/api/hooks/media';
import { PujaImageUpload } from './PujaImageUpload';
import { PujaCityAvailability } from './PujaCityAvailability';
import {
  useArchivePuja,
  useAxisValues,
  useCategories,
  usePatchPuja,
  usePuja,
  usePujas,
  useReplaceSubList,
  useApprovePujaHindi,
  useRestorePuja,
  type PujaDetail,
} from '../lib/api/hooks/catalogue';
import {
  useCreatePackage,
  usePackages,
  useReplacePackageList,
  useRetirePackage,
  useSetPackageActive,
  type PackageListEntry,
  type PackageRow,
} from '../lib/api/hooks/pricing';
import { AuditTrailPanel } from '../components/audit';
import { LintHint } from '../components/lintHint';
import { useLintRules } from '../lib/api/hooks/contentLint';
import { lintText } from '../lib/contentLint';

const PAGE_SIZE = 50;

/** The service modes a puja can be offered in. The server's enum, not a label map. */
const MODES = ['at_home', 'at_temple', 'online'] as const;

/**
 * The participant matrix's closed field set (`ParticipantFieldName`) and the three requirement
 * levels (`FieldRequirement`). Both are schema contracts rather than ops dials: each field maps
 * 1:1 to a `booking_participant` column, and a puja cannot invent a question. They are listed
 * here because the form has to render one control per field — and any field the server returns
 * that is missing from this list is rendered too, so a new column never quietly vanishes.
 */
const FIELD_NAMES = [
  'full_name',
  'gotra',
  'nakshatra',
  'rashi',
  'date_of_birth',
  'birth_place',
  'deceased_details',
] as const;
const REQUIREMENTS = ['required', 'optional', 'off'] as const;

/** What the Primary select shows when nothing is chosen. Picking it clears the flag. */
const NO_PRIMARY = '— none —';

/** A linked value the taxonomy no longer offers still has to render as something. */
function labelOf(options: readonly { id: number; label: string }[], id: number): string {
  return options.find((o) => o.id === id)?.label ?? String(id);
}

/**
 * Server-side filters. `GET /v1/admin/pujas` takes `is_active`, `include_archived`, `page` and
 * `page_size` — there is no text search, and filtering one page in the client would look through
 * fifty rows and confidently report no match for a puja on page two.
 */
const TABS = [
  { key: 'all', label: 'All' },
  { key: 'active', label: 'Active' },
  { key: 'inactive', label: 'Inactive' },
  { key: 'archived', label: 'Archived' },
] as const;
const TAB_KEYS = TABS.map((t) => t.key);

export interface PujaFilter {
  is_active?: boolean;
  include_archived?: boolean;
}

/** Exported for its own test — the tab is a pair of server flags, not a client predicate. */
export function tabToFilter(tab: string): PujaFilter {
  if (tab === 'active') return { is_active: true };
  if (tab === 'inactive') return { is_active: false };
  if (tab === 'archived') return { include_archived: true };
  return {};
}

const DETAIL_TABS = [
  'Definition',
  'Copy',
  'Logistics',
  'Preparation',
  'Tiers',
  'Steps',
  'FAQs',
  'Classification',
  'Links',
  'Participants',
  'Evidence',
  'Cities',
  'History',
];

/** `PackageTier`: a closed set of three, because the comparison screen is built around it. */
const TIERS = ['basic', 'standard', 'premium'] as const;

/**
 * A whole-list replace over a closed option set, rendered as toggles.
 *
 * ⛔ **The option set comes from the server, never from a constant here.** These are taxonomy
 * tables an operator edits on the taxonomy screen; a hard-coded copy would be right on the day it
 * was written and silently short a deity somebody added the week after.
 *
 * **Nothing is written until Save.** Every one of these endpoints is a replace-the-whole-list
 * PUT, so a toggle that saved on click would let one mis-tap send a set with a member missing —
 * and the operator would watch the row vanish with no way to say they had not meant it. This is
 * the "half-built picker that writes a whole-list replace" the previous note in this file warned
 * about, and staging the edit is what answers it.
 *
 * `primary` is a second, exclusive choice over the same set, because occasions and deities each
 * need exactly one. The server refuses a set without one and the refusal is surfaced verbatim —
 * this component does not re-implement that rule, it just never leaves the flag pointing at
 * something no longer selected.
 */
export function LinkPicker({
  scope,
  resetKey,
  title,
  note,
  options,
  loading,
  failed,
  selected,
  primaryId,
  withPrimary,
  onSave,
}: {
  scope: Scope;
  /** The puja being edited. Re-seeding keys on THIS, never on the arrays — see the effect. */
  resetKey: number;
  title: string;
  note: string;
  options: readonly { id: number; label: string }[];
  loading: boolean;
  failed: boolean;
  selected: readonly number[];
  primaryId: number | null;
  withPrimary: boolean;
  onSave: (ids: readonly number[], primary: number | null) => void;
}) {
  const [ids, setIds] = useState<readonly number[]>(selected);
  const [primary, setPrimary] = useState<number | null>(primaryId);
  const editable = can(scope, 'catalogue_content');
  /* ⛔ Keyed on the puja, NOT on `selected`. Those arrays are rebuilt on every parent render, so
     depending on them re-seeded the draft whenever anything refetched — and saving one list
     invalidates the detail, which silently threw away a sibling editor's staged edit. */
  useEffect(() => {
    setIds(selected);
    setPrimary(primaryId);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- see the note above
  }, [resetKey]);

  const dirty =
    ids.length !== selected.length ||
    ids.some((id) => !selected.includes(id)) ||
    primary !== primaryId;

  return (
    <Section title={title}>
      <Note>{note}</Note>
      {loading && <LoadingState message="Reading the list…" />}
      {failed && <ErrorState title="Could not read the list" cause="The options did not load." />}
      {!loading && !failed && options.length === 0 && (
        <EmptyState
          icon="book-open"
          title="Nothing to choose from"
          body="Add values on the taxonomy screen first."
        />
      )}
      {options.length > 0 && (
        <div style={{ display: 'flex', gap: 'var(--sp-sm)', flexWrap: 'wrap' }}>
          {options.map((o) => {
            const on = ids.includes(o.id);
            return (
              <Chip
                key={o.id}
                variant="filter"
                label={withPrimary && primary === o.id ? `${o.label} · primary` : o.label}
                selected={on}
                onClick={() => {
                  setIds(on ? ids.filter((id) => id !== o.id) : [...ids, o.id]);
                  /* Dropping the primary drops the flag with it. Left pointing at something no
                     longer in the set, the save is refused for a reason the screen never showed. */
                  if (on && primary === o.id) setPrimary(null);
                  else if (!on && withPrimary && primary === null) setPrimary(o.id);
                }}
              />
            );
          })}
        </div>
      )}
      {withPrimary && ids.length > 0 && (
        <div style={{ marginTop: 'var(--sp-md)' }}>
          {/* `— none —` is a real choice, not padding: the server refuses a set without a primary
              and says so, and an operator who wants to change their mind about which one it is
              needs a way to clear it that is not "deselect the value entirely". */}
          <Select
            density="admin"
            label="Primary"
            options={[NO_PRIMARY, ...ids.map((id) => labelOf(options, id))]}
            value={primary === null ? NO_PRIMARY : labelOf(options, primary)}
            disabled={!editable}
            onChange={(label) =>
              setPrimary(ids.find((id) => labelOf(options, id) === label) ?? null)
            }
          />
        </div>
      )}
      <div style={{ marginTop: 'var(--sp-md)' }}>
        <ScopeAction
          scope={scope}
          need="catalogue_content"
          size="sm"
          disabled={!dirty}
          onClick={() => onSave(ids, primary)}
        >
          Save
        </ScopeAction>
      </div>
    </Section>
  );
}

/**
 * The participant matrix: which sensitive details this puja may ask for.
 *
 * **`off` is the default and the default is the point** (doc 15 §3) — a field with no row is
 * never collected and does not even render on the app. So this screen sends only the fields set
 * to `required` or `optional`; switching one to `off` is what removing its row means.
 *
 * **`full_name` is fixed at `required` and the control says so** rather than offering a choice
 * the server will refuse: a participant with no name is a row nobody can act on.
 *
 * ⛔ The seven names are the schema's, not a preference — each maps 1:1 to a `booking_participant`
 * column and the set is closed as a data-minimisation contract. Any field the server returns that
 * is not in this list is still rendered, so a new column never silently disappears from the form.
 */
function ParticipantMatrix({
  scope,
  resetKey,
  fields,
  onSave,
}: {
  scope: Scope;
  /** The puja being edited — the re-seed key, for the reason `LinkPicker` records. */
  resetKey: number;
  fields: readonly { field_name: string; requirement: string }[];
  onSave: (items: readonly { field_name: string; requirement: string }[]) => void;
}) {
  const known = [...FIELD_NAMES, ...fields.map((f) => f.field_name).filter((n) => !FIELD_NAMES.includes(n as never))];
  const current = (name: string) =>
    name === 'full_name' ? 'required' : (fields.find((f) => f.field_name === name)?.requirement ?? 'off');
  const [draft, setDraft] = useState<Record<string, string>>({});
  // Keyed on the puja, see LinkPicker.
  useEffect(() => setDraft({}), [resetKey]);
  const valueOf = (name: string) => draft[name] ?? current(name);
  const dirty = Object.keys(draft).some((name) => draft[name] !== current(name));

  return (
    <Section title="Which participant details this puja may ask for">
      <Note>
        Collection is opt-in per field. Anything left off is never collected and never rendered —
        that is data minimisation as a default rather than a rule somebody has to remember.
      </Note>
      {known.map((name) => (
        <Select
          key={name}
          density="admin"
          label={name === 'full_name' ? 'full name (always required)' : name.replace(/_/g, ' ')}
          options={REQUIREMENTS}
          value={valueOf(name)}
          disabled={name === 'full_name' || !can(scope, 'catalogue_content')}
          onChange={(v) => setDraft({ ...draft, [name]: v })}
        />
      ))}
      <div style={{ marginTop: 'var(--sp-md)' }}>
        <ScopeAction
          scope={scope}
          need="catalogue_content"
          size="sm"
          disabled={!dirty}
          onClick={() =>
            onSave(
              known
                .map((name) => ({ field_name: name, requirement: valueOf(name) }))
                /* `off` means "no row", so it is sent as an absence rather than as a value. */
                .filter((f) => f.requirement !== 'off'),
            )
          }
        >
          Save matrix
        </ScopeAction>
      </div>
    </Section>
  );
}

/** The server's evidence types (`EvidenceType`), in the order a pandit captures them. */
const EVIDENCE_TYPES = ['pre_puja_photo', 'completion_photo', 'proof_video', 'temple_receipt', 'other'] as const;

/**
 * Which completion evidence this puja asks for (doc 32 §2). The default is the opposite of the
 * participant matrix's, deliberately: **a type with no row is accepted, as optional**, so a puja
 * nobody has configured never refuses a photograph. Only `off` refuses an upload
 * (`evidence_not_expected`) — video at a rite where filming is not appropriate, say. It governs
 * future uploads only; evidence already accepted is never touched.
 */
function EvidenceChecklist({
  scope,
  resetKey,
  items,
  onSave,
}: {
  scope: Scope;
  resetKey: number;
  items: readonly { evidence_type: string; requirement: string }[];
  onSave: (items: readonly { evidence_type: string; requirement: string }[]) => void;
}) {
  const known = [
    ...EVIDENCE_TYPES,
    ...items.map((i) => i.evidence_type).filter((t) => !EVIDENCE_TYPES.includes(t as never)),
  ];
  const current = (type: string) => items.find((i) => i.evidence_type === type)?.requirement ?? 'optional';
  const [draft, setDraft] = useState<Record<string, string>>({});
  // Keyed on the puja, see LinkPicker.
  useEffect(() => setDraft({}), [resetKey]);
  const valueOf = (type: string) => draft[type] ?? current(type);
  const dirty = Object.keys(draft).some((type) => draft[type] !== current(type));

  return (
    <Section title="Which completion evidence this puja asks for">
      <Note>
        Anything not set is accepted as optional. Off refuses the upload; required shows on the
        pandit’s checklist and is what release looks for.
      </Note>
      {known.map((type) => (
        <Select
          key={type}
          density="admin"
          label={type.replace(/_/g, ' ')}
          options={REQUIREMENTS}
          value={valueOf(type)}
          disabled={!can(scope, 'catalogue_content')}
          onChange={(v) => setDraft({ ...draft, [type]: v })}
        />
      ))}
      <div style={{ marginTop: 'var(--sp-md)' }}>
        <ScopeAction
          scope={scope}
          need="catalogue_content"
          size="sm"
          disabled={!dirty}
          onClick={() =>
            onSave(
              known
                .map((type) => ({ evidence_type: type, requirement: valueOf(type) }))
                /* `optional` is what no row means, so it is sent as an absence. */
                .filter((i) => i.requirement !== 'optional'),
            )
          }
        >
          Save evidence checklist
        </ScopeAction>
      </div>
    </Section>
  );
}

/**
 * The related rail and the media set — two lists whose members are rows in other tables, so both
 * are picked rather than typed.
 *
 * **A related puja is picked by id from the catalogue itself**, because the write shape is
 * `related_puja_id` and a typed slug is a slug somebody can mistype into a link that 404s on a
 * page nobody checks.
 *
 * **Media is picked from the library by `storage_key`, never by URL.** The read side carries the
 * key for exactly this reason (CSR-027): a presigned URL expires, so a set rebuilt from one would
 * save links that were dead before the operator pressed the button.
 */
function PickedListEditor({
  scope,
  resetKey,
  title,
  note,
  emptyBody,
  options,
  loading = false,
  failed = false,
  rows,
  addLabel,
  secondLabel,
  secondPlaceholder,
  secondRequired = false,
  onSave,
}: {
  scope: Scope;
  /** The puja being edited — the re-seed key, for the reason `LinkPicker` records. */
  resetKey: number;
  title: string;
  note: string;
  emptyBody: string;
  options: readonly { id: string; label: string }[];
  loading?: boolean;
  failed?: boolean;
  rows: readonly { id: string; second: string }[];
  addLabel: string;
  secondLabel: string;
  secondPlaceholder: string;
  /** The second field must be filled before a row is added or the list saved (alt text). */
  secondRequired?: boolean;
  onSave: (rows: readonly { id: string; second: string }[]) => void;
}) {
  const [draft, setDraft] = useState<readonly { id: string; second: string }[]>(rows);
  const [pick, setPick] = useState('');
  /* Resolved here rather than inside the handler: the Add button is disabled until it resolves,
     so a `chosen === undefined` guard in the click would be a branch the UI cannot reach. */
  const chosen = options.find((o) => o.label === pick);
  const [second, setSecond] = useState('');
  // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on the puja, see LinkPicker
  useEffect(() => setDraft(rows), [resetKey]);
  const editable = can(scope, 'catalogue_content');
  const labelFor = (id: string) => options.find((o) => o.id === id)?.label ?? id;
  const dirty =
    draft.length !== rows.length ||
    draft.some((r, i) => r.id !== rows[i]?.id || r.second !== rows[i]?.second);
  const blank = (text: string) => secondRequired && text.trim() === '';

  return (
    <Section title={title}>
      <Note>{note}</Note>
      {loading && <LoadingState message="Reading the list…" />}
      {failed && <ErrorState title="Could not read the list" cause="The options did not load." />}
      {!loading && !failed && draft.length === 0 && (
        <EmptyState icon="book-open" title="Nothing linked yet" body={emptyBody} />
      )}
      {draft.map((r) => (
        <div key={r.id} style={{ display: 'flex', gap: 'var(--sp-md)', alignItems: 'center' }}>
          <span style={{ flex: 1 }}>
            {labelFor(r.id)}
            {r.second ? ` · ${r.second}` : ''}
          </span>
          <ScopeAction
            scope={scope}
            need="catalogue_content"
            size="sm"
            variant="ghost"
            onClick={() => setDraft(draft.filter((d) => d.id !== r.id))}
          >
            Remove
          </ScopeAction>
        </div>
      ))}
      {options.length > 0 && (
        <div style={{ display: 'flex', gap: 'var(--sp-md)', alignItems: 'end', marginTop: 'var(--sp-md)' }}>
          <Select
            density="admin"
            label={addLabel}
            options={options.filter((o) => !draft.some((d) => d.id === o.id)).map((o) => o.label)}
            value={pick}
            disabled={!editable}
            onChange={setPick}
            style={{ flex: 1 }}
          />
          <Input
            density="admin"
            label={secondLabel}
            value={second}
            onChange={setSecond}
            placeholder={secondPlaceholder}
          />
          <ScopeAction
            scope={scope}
            need="catalogue_content"
            size="sm"
            variant="secondary"
            disabled={chosen === undefined || blank(second)}
            onClick={() => {
              setDraft([...draft, { id: chosen!.id, second: second.trim() }]);
              setPick('');
              setSecond('');
            }}
          >
            {addLabel}
          </ScopeAction>
        </div>
      )}
      <div style={{ marginTop: 'var(--sp-md)' }}>
        <ScopeAction
          scope={scope}
          need="catalogue_content"
          size="sm"
          disabled={!dirty || draft.some((r) => blank(r.second))}
          onClick={() => onSave(draft)}
        >
          Save
        </ScopeAction>
      </div>
    </Section>
  );
}

/**
 * Edited lines back as list entries. The editor shows the English label and detail; the Hindi
 * label and the icon are carried over from the line with the same English label, so editing a
 * detail does not silently wipe a translation nobody could see on this screen.
 */
export function toListEntries(
  rows: readonly OrderedRow[],
  previous: readonly PackageListEntry[],
): PackageListEntry[] {
  return rows.map((r) => {
    const was = previous.find((p) => p.label_en === r.primary);
    return {
      label_en: r.primary,
      label_hi: was?.label_hi ?? null,
      detail_en: r.secondary || null,
      icon_key: was?.icon_key ?? null,
    };
  });
}

/**
 * A puja's tiers: make one, say what it includes and leaves out, switch it off, retire it.
 *
 * **Retire is guarded; deactivate is not.** Deactivating hides a tier and is undone by a click;
 * retiring removes it for good, and the server refuses while a booking still points at it
 * (`409 package_in_use`) — that refusal is shown verbatim.
 */
function TierPanel({ scope, pujaId }: { scope: Scope; pujaId: number }) {
  const { toast } = useConsole();
  const packages = usePackages(pujaId);
  const create = useCreatePackage();
  const setActive = useSetPackageActive();
  const replaceList = useReplacePackageList();
  const retire = useRetirePackage();
  const [retiring, setRetiring] = useState<PackageRow | null>(null);
  const rows = packages.data?.items ?? [];
  const free = TIERS.filter((t) => !rows.some((r) => r.tier === t));
  const [tier, setTier] = useState('');
  const [name, setName] = useState('');
  const [minutes, setMinutes] = useState('90');
  const [pandits, setPandits] = useState('1');
  const fail = (e: unknown) => toast({ tone: 'error' as const, message: errorMessage(e) });
  const outcome = (message: string) => ({
    onSuccess: () => toast({ tone: 'success' as const, message }),
    onError: fail,
  });
  const chosenTier = free.find((t) => t === tier) ?? free[0];

  return (
    <>
      {packages.isPending && <LoadingState message="Reading tiers…" />}
      {packages.isError && (
        <ErrorState title="Could not read tiers" cause={errorMessage(packages.error)} />
      )}
      {packages.isSuccess && rows.length === 0 && (
        <EmptyState
          icon="book-open"
          title="No tier yet"
          body="A devotee cannot book this puja until it has a tier with a published price."
        />
      )}
      {rows.map((row) => (
        <Section key={row.id} title={`${row.tier} · ${row.name}`}>
          <div style={{ display: 'flex', gap: 'var(--sp-md)', alignItems: 'center', flexWrap: 'wrap' }}>
            <StatusChip
              tone={row.is_active ? 'success' : 'neutral'}
              label={row.is_active ? 'offered' : 'switched off'}
            />
            <span>
              {row.duration_minutes} min · {row.pandit_count}{' '}
              {row.pandit_count === 1 ? 'pandit' : 'pandits'}
            </span>
            <ScopeAction
              scope={scope}
              need="catalogue_content"
              size="sm"
              variant="secondary"
              onClick={() =>
                setActive.mutate(
                  { packageId: row.id, active: !row.is_active },
                  outcome(row.is_active ? 'Tier switched off' : 'Tier offered again'),
                )
              }
            >
              {row.is_active ? `Switch off ${row.tier}` : `Offer ${row.tier}`}
            </ScopeAction>
            <ScopeAction
              scope={scope}
              need="catalogue_content"
              size="sm"
              variant="ghost"
              onClick={() => setRetiring(row)}
            >
              {`Retire ${row.tier}`}
            </ScopeAction>
          </div>
          {(['inclusions', 'exclusions'] as const).map((list) => (
            <div key={list} style={{ marginTop: 'var(--sp-md)' }}>
              <Kicker>{list === 'inclusions' ? 'What it includes' : 'What it leaves out'}</Kicker>
              <OrderedListEditor
                rows={row[list].map((e) => ({ primary: e.label_en, secondary: e.detail_en ?? '' }))}
                addLabel={list === 'inclusions' ? 'Inclusion' : 'Exclusion'}
                disabled={!can(scope, 'catalogue_content')}
                onSave={(edited: OrderedRow[]) =>
                  replaceList.mutate(
                    { packageId: row.id, list, items: toListEntries(edited, row[list]) },
                    outcome(list === 'inclusions' ? 'Inclusions saved' : 'Exclusions saved'),
                  )
                }
              />
            </div>
          ))}
        </Section>
      ))}

      {packages.isSuccess && chosenTier !== undefined && (
        <Section title="Add a tier" note="Prices are set per city on the Pricing screen.">
          <div style={{ display: 'flex', gap: 'var(--sp-md)', alignItems: 'end', flexWrap: 'wrap' }}>
            <Select label="Tier" value={chosenTier} options={free} onChange={setTier} />
            <Input label="Tier name" value={name} onChange={setName} />
            <Input label="Minutes" inputMode="numeric" value={minutes} onChange={setMinutes} />
            <Input label="Pandits" inputMode="numeric" value={pandits} onChange={setPandits} />
            <ScopeAction
              scope={scope}
              need="catalogue_content"
              disabled={name.trim() === ''}
              onClick={() =>
                create.mutate(
                  {
                    pujaId,
                    body: {
                      tier: chosenTier,
                      name: name.trim(),
                      duration_minutes: Number(minutes),
                      pandit_count: Number(pandits),
                    },
                  },
                  {
                    onError: fail,
                    onSuccess: () => {
                      toast({ tone: 'success', message: 'Tier added' });
                      setName('');
                      setTier('');
                    },
                  },
                )
              }
            >
              Add tier
            </ScopeAction>
          </div>
        </Section>
      )}

      <ConfirmAction
        open={retiring !== null}
        title={`Retire the ${retiring?.tier ?? ''} tier?`}
        phrase="RETIRE"
        tone="error"
        consequences={[
          'The tier stops being offered for good; offering it again means adding it afresh.',
          'Refused while a booking still points at it — switch it off instead.',
        ]}
        onCancel={() => setRetiring(null)}
        onConfirm={() => {
          const row = retiring!;
          setRetiring(null);
          retire.mutate(row.id, outcome('Tier retired'));
        }}
      />
    </>
  );
}

type PujaPatch = components['schemas']['PujaPatchIn'];

/** Only the fields that moved. A frozen slug sent back unchanged would still be a write the
    server has to judge, and an unchanged name would clear nothing but cost an audit row. */
function changed<T extends Record<string, unknown>>(next: T, base: T): Partial<T> {
  return Object.fromEntries(Object.entries(next).filter(([k, v]) => v !== base[k])) as Partial<T>;
}

/** A whole number in range, or the reason it is not one. */
function wholeNumber(text: string, min: number, max: number): number | string {
  if (!/^\d+$/.test(text.trim())) return 'A whole number';
  const n = Number(text);
  return n < min || n > max ? `Between ${min} and ${max}` : n;
}

/** What a picker offers: the active values, plus any retired one this puja still links — so it
    shows, and can be taken off — but never a retired one to add. The live deity picker offered a
    retired E2E fixture row to every puja. */
function pickable(
  items: readonly { id: number; name_en: string; is_active: boolean }[],
  linked: readonly number[],
): { id: number; label: string }[] {
  return items.filter((o) => o.is_active || linked.includes(o.id)).map((o) => ({ id: o.id, label: o.name_en }));
}

function DefinitionEditor({
  d,
  scope,
  categories,
  onSave,
}: {
  d: PujaDetail;
  scope: Scope;
  categories: { id: number; name_en: string }[];
  onSave: (patch: PujaPatch) => void;
}) {
  const base = {
    name_en: d.name_en,
    name_hi: d.name_hi,
    sanskrit_name: d.sanskrit_name ?? null,
    puja_category_id: d.puja_category_id,
    slug: d.slug,
    is_launch: d.is_launch,
  };
  const [draft, setDraft] = useState(base);
  /* Re-seeded on every read, so a save (or another admin's) is what the form shows next. */
  // eslint-disable-next-line react-hooks/exhaustive-deps -- `base` is rebuilt from `d` each render
  useEffect(() => setDraft(base), [d]);
  const set = (patch: Partial<typeof base>) => setDraft((cur) => ({ ...cur, ...patch }));
  const diff = changed(draft, base);
  const blank = draft.name_en.trim() === '' || draft.name_hi.trim() === '' || draft.slug.trim() === '';

  return (
    <Section title="What this ritual is">
      <div style={{ display: 'grid', gap: 'var(--sp-md)', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))' }}>
        {/* Two name fields, never one translated into the other. */}
        <Input
          label="Name (transliteration)"
          value={draft.name_en}
          onChange={(v) => set({ name_en: v })}
          error={draft.name_en.trim() === '' ? 'Required' : undefined}
          density="admin"
        />
        <Input
          label="नाम (Devanagari)"
          value={draft.name_hi}
          onChange={(v) => set({ name_hi: v })}
          error={draft.name_hi.trim() === '' ? 'Required' : undefined}
          density="admin"
        />
        <Input
          label="Sanskrit"
          value={draft.sanskrit_name ?? ''}
          onChange={(v) => set({ sanskrit_name: v.trim() === '' ? null : v })}
          density="admin"
        />
        <Select
          label="Category"
          value={categories.find((c) => c.id === draft.puja_category_id)?.name_en}
          options={categories.map((c) => c.name_en)}
          onChange={(name) => set({ puja_category_id: categories.find((c) => c.name_en === name)!.id })}
        />
        <Input
          label="Slug"
          value={draft.slug}
          onChange={(v) => set({ slug: v })}
          disabled={d.slug_frozen}
          error={draft.slug.trim() === '' ? 'Required' : undefined}
          helper={
            d.slug_frozen
              ? 'Frozen because the page is published. Changing it would break every inbound link and search result already pointing at it.'
              : undefined
          }
          density="admin"
        />
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-sm)' }}>
          <Toggle on={draft.is_launch} label="Launch set" onChange={(on) => set({ is_launch: on })} />
          <span style={{ fontSize: 'var(--fs-admin-cell)', color: 'var(--c-ink2)' }}>Launch set</span>
        </div>
      </div>
      <div style={{ marginTop: 'var(--sp-md)' }}>
        <ScopeAction
          scope={scope}
          need="catalogue_content"
          size="sm"
          disabled={blank || Object.keys(diff).length === 0}
          onClick={() => onSave(diff)}
        >
          Save definition
        </ScopeAction>
      </div>
    </Section>
  );
}

function LogisticsEditor({
  d,
  scope,
  onSave,
}: {
  d: PujaDetail;
  scope: Scope;
  onSave: (patch: PujaPatch) => void;
}) {
  const seed = () => ({
    min: String(d.duration_minutes_min),
    max: String(d.duration_minutes_max),
    pandits: String(d.pandit_count_required),
    samagri: d.samagri_note ?? '',
    sort: String(d.sort_order),
  });
  const [draft, setDraft] = useState(seed);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- `seed` reads `d`
  useEffect(() => setDraft(seed()), [d]);
  const set = (patch: Partial<ReturnType<typeof seed>>) => setDraft((cur) => ({ ...cur, ...patch }));

  const min = wholeNumber(draft.min, 1, 10000);
  const max = wholeNumber(draft.max, 1, 10000);
  const pandits = wholeNumber(draft.pandits, 1, 50);
  const sort = wholeNumber(draft.sort, 0, 65535);
  const order =
    typeof min === 'number' && typeof max === 'number' && max < min ? 'No shorter than the minimum' : undefined;
  const errs = [min, max, pandits, sort].filter((v): v is string => typeof v === 'string');
  const valid = errs.length === 0 && !order;
  const diff = valid
    ? changed(
        {
          duration_minutes_min: min as number,
          duration_minutes_max: max as number,
          pandit_count_required: pandits as number,
          samagri_note: draft.samagri.trim() === '' ? null : draft.samagri,
          sort_order: sort as number,
        },
        {
          duration_minutes_min: d.duration_minutes_min,
          duration_minutes_max: d.duration_minutes_max,
          pandit_count_required: d.pandit_count_required,
          samagri_note: d.samagri_note ?? null,
          sort_order: d.sort_order,
        },
      )
    : {};
  const err = (v: number | string) => (typeof v === 'string' ? v : undefined);

  return (
    <div style={{ display: 'grid', gap: 'var(--sp-md)', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))' }}>
      <Input label="Shortest, minutes" value={draft.min} onChange={(v) => set({ min: v })} inputMode="numeric" error={err(min)} density="admin" />
      <Input label="Longest, minutes" value={draft.max} onChange={(v) => set({ max: v })} inputMode="numeric" error={err(max) ?? order} density="admin" />
      <Input label="Pandits required" value={draft.pandits} onChange={(v) => set({ pandits: v })} inputMode="numeric" error={err(pandits)} density="admin" />
      <Input label="Sort order" value={draft.sort} onChange={(v) => set({ sort: v })} inputMode="numeric" error={err(sort)} density="admin" />
      <Input
        label="Samagri"
        value={draft.samagri}
        onChange={(v) => set({ samagri: v })}
        helper="Who brings the materials, in a line. Blank means not stated."
        density="admin"
        style={{ gridColumn: '1 / -1' }}
      />
      <div>
        <ScopeAction
          scope={scope}
          need="catalogue_content"
          size="sm"
          disabled={Object.keys(diff).length === 0}
          onClick={() => onSave(diff)}
        >
          Save logistics
        </ScopeAction>
      </div>
    </div>
  );
}

export function PujaCatalogue() {
  const { scope, toast } = useConsole();
  const [tab, setTab] = useUrlTab('pujas', TAB_KEYS, 'all');
  const [detailTab, setDetailTab] = useState('Definition');
  const [archiving, setArchiving] = useState(false);
  const [approvingHindi, setApprovingHindi] = useState(false);
  const [significance, setSignificance] = useState('');
  const [tagline, setTagline] = useState('');
  const [creating, setCreating] = useState(false);
  /* The same phrase list the server enforces, checked as the editor types (doc 10 §2.1). */
  const lintRules = useLintRules().data?.items ?? [];
  const copyHint = (text: string) => <LintHint text={text} hits={lintText(text, lintRules)} />;

  const pujas = usePujas({ page: 1, page_size: PAGE_SIZE, ...tabToFilter(tab) });
  const rows = pujas.data?.items ?? [];
  const { focus, setFocus, open, setOpen } = useQueue(rows, { key: tab });

  const detail = usePuja(open?.id);
  /* The option sets behind the three pickers. Each is a taxonomy table the operator edits on
     its own screen — read here, never restated. */
  const deityOptions = useAxisValues('deities');
  const occasionOptions = useAxisValues('occasions');
  const traditionOptions = useAxisValues('sampradayas');
  /* Media comes from the library by storage key. The related rail picks from `rows` — the very
     list this screen is already rendering — so it needs no second query and can never be
     separately pending. */
  const mediaOptions = useMediaLibrary({ page: 1, page_size: PAGE_SIZE, status: 'ready' });
  const categories = useCategories();
  const patch = usePatchPuja();
  const archive = useArchivePuja();
  const restore = useRestorePuja();
  const approveHindi = useApprovePujaHindi();
  const replace = useReplaceSubList();

  /* The editable copy is seeded from the server every time a puja opens. Keeping local state that
     survived a switch would show one puja's words under another's name. */
  useEffect(() => {
    setSignificance(detail.data?.significance ?? '');
    setTagline(detail.data?.tagline_en ?? '');
  }, [detail.data]);

  const outcome = (message: string, tone: 'success' | 'info' | 'error' = 'success') => ({
    onSuccess: () => toast({ tone, message }),
    onError: (e: unknown) => toast({ tone: 'error' as const, message: errorMessage(e) }),
  });

  const categoryName = (id: number) =>
    categories.data?.find((c) => c.id === id)?.name_en ?? String(id);

  /* The header follows the detail read, not the list row that opened it: the row is a snapshot,
     and on the live pass a Deactivate that succeeded left the chip saying "active" and the
     button offering Deactivate again. A detail still loading — or still the last puja's —
     falls back to the row. */
  const live = open && (detail.data?.id === open.id ? detail.data : open);

  const archiveDialog = live && (
    <ConfirmAction
      open={archiving}
      title={`Archive ${live.name_en}?`}
      phrase="ARCHIVE"
      tone="error"
      reason
      consequences={[
        'It leaves every picker and stops being matchable. It is NOT deleted: pandit skills and bookings pointing at it still resolve.',
        'The public page goes at the next site build. Restore puts all of it back.',
      ]}
      onCancel={() => setArchiving(false)}
      onConfirm={(reason: string) => {
        setArchiving(false);
        archive.mutate({ pujaId: live.id, reason }, outcome(`${live.name_en} archived`, 'info'));
      }}
      confirmLabel="Archive puja"
    />
  );

  if (live) {
    const d = detail.data;
    return (
      <DetailPage
        title={live.name_en}
        code={live.slug}
        status={
          <StatusChip
            tone={live.deleted_at ? 'error' : live.is_active ? 'success' : 'neutral'}
            label={live.deleted_at ? 'archived' : live.is_active ? 'active' : 'inactive'}
          />
        }
        tabs={DETAIL_TABS}
        activeTab={detailTab}
        onTab={setDetailTab}
        onClose={() => setOpen(null)}
        actions={
          <div style={{ display: 'flex', gap: 'var(--sp-sm)', flexWrap: 'wrap' }}>
            {live.deleted_at ? (
              <ScopeAction
                scope={scope}
                need="catalogue_content"
                size="sm"
                onClick={() => restore.mutate({ pujaId: live.id }, outcome(`${live.name_en} restored`))}
              >
                Restore
              </ScopeAction>
            ) : (
              <>
                <ScopeAction
                  scope={scope}
                  need="catalogue_content"
                  size="sm"
                  onClick={() =>
                    patch.mutate(
                      { pujaId: live.id, patch: { is_active: !live.is_active } },
                      outcome(live.is_active ? 'Taken off the catalogue' : 'Back on the catalogue'),
                    )
                  }
                >
                  {live.is_active ? 'Deactivate' : 'Activate'}
                </ScopeAction>
                <ScopeAction scope={scope} need="catalogue_content" size="sm" onClick={() => setArchiving(true)}>
                  Archive
                </ScopeAction>
              </>
            )}
          </div>
        }
      >
        {detail.isPending && <LoadingState message="Reading this puja…" />}
        {detail.isError && <ErrorState title="Could not read this puja" cause={errorMessage(detail.error)} />}

        {d && detailTab === 'Definition' && (
          <DefinitionEditor
            d={d}
            scope={scope}
            categories={categories.data ?? []}
            onSave={(p) => patch.mutate({ pujaId: live.id, patch: p }, outcome('Definition saved'))}
          />
        )}

        {d && detailTab === 'Copy' && (
          <Section title="What a devotee reads">
            <Note>
              Traditional and scriptural significance only. No outcome claim — not health, wealth,
              jobs, court cases, marriage, fertility or “removes dosh”. The server’s content lint
              runs on save and refuses with the rule that was broken.
            </Note>
            <div style={{ display: 'grid', gap: 'var(--sp-md)', marginTop: 'var(--sp-md)' }}>
              <div>
                <Input label="Tagline" value={tagline} onChange={setTagline} density="admin" />
                {copyHint(tagline)}
              </div>
              <div>
                <Input
                  label="Significance"
                  value={significance}
                  onChange={setSignificance}
                  density="admin"
                  multiline
                  helper="Traditional and scriptural significance only."
                />
                {copyHint(significance)}
              </div>
              <div>
                <ScopeAction
                  scope={scope}
                  need="catalogue_content"
                  size="sm"
                  disabled={significance === d.significance && tagline === (d.tagline_en ?? '')}
                  onClick={() =>
                    patch.mutate(
                      { pujaId: live.id, patch: { significance, tagline_en: tagline } },
                      outcome('Copy saved · lint passed'),
                    )
                  }
                >
                  Save copy
                </ScopeAction>
              </div>
            </div>
            {d.preparation_notes && (
              <p style={{ marginTop: 'var(--sp-md)', color: 'var(--c-ink2)' }}>{d.preparation_notes}</p>
            )}
          </Section>
        )}

        {d && detailTab === 'Copy' && (
          <Section title="Hindi review">
            <KV
              rows={[
                [
                  'Status',
                  d.hindi_review.hi_reviewed_at
                    ? `Reviewed by admin #${d.hindi_review.hi_reviewed_by_admin_id} · ${d.hindi_review.hi_reviewed_at.slice(0, 16).replace('T', ' ')} UTC`
                    : 'Not reviewed — a launch puja’s Hindi does not publish until a Hindi reader approves it',
                ],
                [
                  'Last Hindi edit',
                  d.hindi_review.hi_edited_at
                    ? `admin #${d.hindi_review.hi_edited_by_admin_id} · ${d.hindi_review.hi_edited_at.slice(0, 16).replace('T', ' ')} UTC`
                    : 'none in the console — seeded copy',
                ],
              ]}
            />
            <div style={{ marginTop: 'var(--sp-sm)' }}>
              <ScopeAction
                scope={scope}
                need="catalogue_content"
                size="sm"
                disabled={Boolean(d.hindi_review.hi_reviewed_at)}
                onClick={() => setApprovingHindi(true)}
              >
                Approve Hindi
              </ScopeAction>
            </div>
            <ConfirmAction
              open={approvingHindi}
              title={`Approve the Hindi of ${live.name_en}?`}
              phrase="APPROVE"
              consequences={[
                'You confirm you have read every Hindi line on this puja: its name, significance and muhurat guidance, and its steps, prep checklist, provisions and FAQs.',
                'You cannot approve Hindi you last edited — another Hindi reader must.',
                'Any later Hindi edit clears this approval, and the page waits for a new one.',
              ]}
              onCancel={() => setApprovingHindi(false)}
              onConfirm={() => {
                setApprovingHindi(false);
                approveHindi.mutate({ pujaId: live.id }, outcome('Hindi approved'));
              }}
              confirmLabel="Approve Hindi"
            />
          </Section>
        )}

        {d && detailTab === 'Logistics' && (
          <Section title="What performing it takes">
            <LogisticsEditor
              d={d}
              scope={scope}
              onSave={(p) => patch.mutate({ pujaId: live.id, patch: p }, outcome('Logistics saved'))}
            />
            <div style={{ marginTop: 'var(--sp-md)' }}>
              <Kicker>Service modes</Kicker>
              <div style={{ display: 'flex', gap: 'var(--sp-xs)', flexWrap: 'wrap', marginTop: 'var(--sp-sm)' }}>
                {MODES.map((mode) => {
                  const on = d.modes.includes(mode);
                  return (
                    <ScopeAction
                      key={mode}
                      scope={scope}
                      need="catalogue_content"
                      size="sm"
                      variant={on ? 'primary' : 'secondary'}
                      onClick={() =>
                        replace.mutate(
                          {
                            pujaId: live.id,
                            list: 'modes',
                            items: on ? d.modes.filter((m) => m !== mode) : [...d.modes, mode],
                          },
                          outcome(on ? `${mode.replace(/_/g, ' ')} removed` : `${mode.replace(/_/g, ' ')} added`),
                        )
                      }
                    >
                      {mode.replace(/_/g, ' ')}
                    </ScopeAction>
                  );
                })}
              </div>
              {/* The six classification links round-trip the same way and are counted here rather
                  than edited: each needs a picker against another table, and a half-built picker
                  that writes a whole-list replace is how a list gets emptied by accident. */}
              <p style={{ marginTop: 'var(--sp-md)', color: 'var(--c-ink3)', fontSize: 'var(--fs-admin-colhead)' }}>
                {count(d.deities.length, 'deity', 'deities')} ·{' '}
                {count(d.occasions.length, 'occasion')} ·{' '}
                {count(d.sampradayas.length, 'tradition')} · {d.media.length} media ·{' '}
                {d.related.length} related · {count(d.booking_fields.length, 'booking field')}. These are
                classification links, edited from their own pickers rather than here.
              </p>
            </div>
          </Section>
        )}
        {d && detailTab === 'Preparation' && (
          <>
            <Section title="The devotee's prep checklist">
              <Note>
                What the household does before the pandit arrives — sent in the pre-visit reminder,
                in this order.
              </Note>
              <PrepChecklistEditor
                rows={d.prep_items}
                disabled={!can(scope, 'catalogue_content')}
                onSave={(items) =>
                  replace.mutate({ pujaId: live.id, list: 'prep-items', items }, outcome('Prep checklist saved'))
                }
              />
            </Section>
            <Section title="What we bring and what the household keeps ready">
              <Note>
                The split the booking confirms and the pandit packs from. A critical line is one the
                puja cannot go ahead without.
              </Note>
              <ProvisionEditor
                rows={d.provisions}
                disabled={!can(scope, 'catalogue_content')}
                onSave={(items) =>
                  replace.mutate({ pujaId: live.id, list: 'provisions', items }, outcome('Provisions saved'))
                }
              />
            </Section>
          </>
        )}
        {d && detailTab === 'Tiers' && <TierPanel scope={scope} pujaId={live.id} />}

        {d && detailTab === 'Steps' && (
          <Section title="What happens, in order">
            <Note>
              The order is the list order — moving a step is what reordering means, and the whole
              list is replaced on save.
            </Note>
            <OrderedListEditor
              rows={d.steps.map((st) => ({ primary: st.title_en, secondary: st.description_en ?? '' }))}
              addLabel="Step"
              disabled={!can(scope, 'catalogue_content')}
              hint={copyHint}
              onSave={(rows: OrderedRow[]) =>
                replace.mutate(
                  {
                    pujaId: live.id,
                    list: 'steps',
                    items: rows.map((r: OrderedRow) => ({
                      title_en: r.primary,
                      title_hi: null,
                      description_en: r.secondary || null,
                      duration_minutes: null,
                    })),
                  },
                  outcome('Steps saved'),
                )
              }
            />
          </Section>
        )}

        {d && detailTab === 'FAQs' && (
          <Section title="Questions a devotee asks">
            <Note>
              Answers ship to the public page and pass the same content lint as the significance —
              no outcome claim, ever.
            </Note>
            <OrderedListEditor
              rows={d.faqs.map((f) => ({ primary: f.question_en, secondary: f.answer_en }))}
              addLabel="FAQ"
              disabled={!can(scope, 'catalogue_content')}
              hint={copyHint}
              onSave={(rows: OrderedRow[]) =>
                replace.mutate(
                  {
                    pujaId: live.id,
                    list: 'faqs',
                    items: rows.map((r: OrderedRow) => ({
                      question_en: r.primary,
                      answer_en: r.secondary,
                      question_hi: null,
                      answer_hi: null,
                    })),
                  },
                  outcome('FAQs saved'),
                )
              }
            />
          </Section>
        )}

        {d && detailTab === 'Classification' && (
          <>
            <LinkPicker
              scope={scope}
              resetKey={live.id}
              title="Deities"
              note="Exactly one is primary — it is the deity the public page names first."
              options={pickable(deityOptions.data?.items ?? [], d.deities.map((x) => x.deity_id))}
              loading={deityOptions.isPending}
              failed={deityOptions.isError}
              selected={d.deities.map((x) => x.deity_id)}
              primaryId={d.deities.find((x) => x.is_primary)?.deity_id ?? null}
              withPrimary
              onSave={(ids, primary) =>
                replace.mutate(
                  {
                    pujaId: live.id,
                    list: 'deities',
                    items: ids.map((id) => ({ deity_id: id, is_primary: id === primary })),
                  },
                  outcome('Deities saved'),
                )
              }
            />
            <LinkPicker
              scope={scope}
              resetKey={live.id}
              title="Occasions"
              note="Exactly one is primary — a puja with no primary occasion has no canonical page."
              options={pickable(occasionOptions.data?.items ?? [], d.occasions.map((x) => x.occasion_id))}
              loading={occasionOptions.isPending}
              failed={occasionOptions.isError}
              selected={d.occasions.map((x) => x.occasion_id)}
              primaryId={d.occasions.find((x) => x.is_primary)?.occasion_id ?? null}
              withPrimary
              onSave={(ids, primary) =>
                replace.mutate(
                  {
                    pujaId: live.id,
                    list: 'occasions',
                    items: ids.map((id) => ({ occasion_id: id, is_primary: id === primary })),
                  },
                  outcome('Occasions saved'),
                )
              }
            />
            <LinkPicker
              scope={scope}
              resetKey={live.id}
              title="Traditions"
              note="No primary here: a puja performed in several sampradayas has no senior one."
              options={pickable(traditionOptions.data?.items ?? [], d.sampradayas.map((x) => x.sampradaya_id))}
              loading={traditionOptions.isPending}
              failed={traditionOptions.isError}
              selected={d.sampradayas.map((x) => x.sampradaya_id)}
              primaryId={null}
              withPrimary={false}
              onSave={(ids) =>
                replace.mutate(
                  {
                    pujaId: live.id,
                    list: 'sampradayas',
                    items: ids.map((id) => ({
                      sampradaya_id: id,
                      /* Kept, not dropped: the note is the one thing on this row a picker cannot
                         reconstruct, and a save that cleared it would lose an operator's words. */
                      variant_note:
                        d.sampradayas.find((x) => x.sampradaya_id === id)?.variant_note ?? null,
                    })),
                  },
                  outcome('Traditions saved'),
                )
              }
            />
          </>
        )}

        {d && detailTab === 'Links' && (
          <>
            <PickedListEditor
              scope={scope}
              resetKey={live.id}
              title="Related pujas"
              note="The rail a devotee follows from this page. Order is the list order."
              emptyBody="Nothing is linked from this puja yet."
              addLabel="Add a related puja"
              options={rows
                .filter((x) => x.id !== live.id)
                .map((x) => ({ id: String(x.id), label: x.name_en }))}
              rows={d.related.map((r) => ({ id: String(r.related_puja_id), second: r.relation }))}
              secondLabel="Relation"
              secondPlaceholder="similar"
              onSave={(rows) =>
                replace.mutate(
                  {
                    pujaId: live.id,
                    list: 'related',
                    items: rows.map((r) => ({
                      related_puja_id: Number(r.id),
                      relation: r.second || 'similar',
                    })),
                  },
                  outcome('Related rail saved'),
                )
              }
            />
            <PujaImageUpload scope={scope} pujaId={live.id} />
            <PickedListEditor
              scope={scope}
              resetKey={live.id}
              title="Media"
              note="Picked from the library by storage key — never by URL, which expires."
              emptyBody="No image is attached to this puja yet."
              addLabel="Add an image"
              /* The library row carries no caption — alt text belongs to the LINK, not to the
                 asset, because the same photo describes different things on different pages. So
                 the picker offers the key and the operator writes the description beside it. */
              options={(mediaOptions.data?.items ?? []).map((m) => ({
                id: m.storage_key,
                label: `${m.storage_key} · ${m.content_type}`,
              }))}
              loading={mediaOptions.isPending}
              failed={mediaOptions.isError}
              rows={d.media.map((m) => ({ id: m.storage_key, second: m.alt_text_en }))}
              secondLabel="Alt text"
              secondPlaceholder="Describe it for someone who cannot see it — not keywords"
              secondRequired
              onSave={(rows) =>
                replace.mutate(
                  {
                    pujaId: live.id,
                    list: 'media',
                    items: rows.map((r) => ({
                      kind: d.media.find((m) => m.storage_key === r.id)?.kind ?? 'photo',
                      storage_key: r.id,
                      /* Required by the editor (admin 24 §2): the storage key once stood in
                         here, and a screen reader reading out a file name has said nothing. */
                      alt_text_en: r.second,
                    })),
                  },
                  outcome('Media saved'),
                )
              }
            />
          </>
        )}

        {d && detailTab === 'Participants' && (
          <ParticipantMatrix
            scope={scope}
            resetKey={live.id}
            fields={d.booking_fields}
            onSave={(items) =>
              replace.mutate(
                { pujaId: live.id, list: 'booking-fields', items: [...items] },
                outcome('Participant matrix saved'),
              )
            }
          />
        )}

        {d && detailTab === 'Evidence' && (
          <EvidenceChecklist
            scope={scope}
            resetKey={live.id}
            items={d.evidence_requirements}
            onSave={(items) =>
              replace.mutate(
                { pujaId: live.id, list: 'evidence-requirements', items: [...items] },
                outcome('Evidence checklist saved'),
              )
            }
          />
        )}

        {detailTab === 'Cities' && <PujaCityAvailability scope={scope} pujaId={live.id} />}
        {detailTab === 'History' && <AuditTrailPanel entityType="puja" entityId={live.id} />}

        {archiveDialog}
      </DetailPage>
    );
  }

  if (creating) {
    return (
      <NewPujaForm
        onDone={(created) => {
          setCreating(false);
          if (created) setOpen(created);
        }}
      />
    );
  }

  return (
    <>
      <FilterBar
        tabs={TABS.map((t) => ({ key: t.key, label: t.label }))}
        tab={tab}
        onTab={setTab}
        right={
          <ScopeAction scope={scope} need="catalogue_content" size="sm" icon="plus" onClick={() => setCreating(true)}>
            New puja
          </ScopeAction>
        }
      />

      {pujas.isPending && <LoadingState message="Reading the catalogue…" />}
      {pujas.isError && <ErrorState title="Could not read the catalogue" cause={errorMessage(pujas.error)} />}
      {pujas.isSuccess && rows.length === 0 && <EmptyState icon="book-open" title="No puja in this view" />}
      {rows.length > 0 && (
        <DataTable
          columns={[
            { key: 'name', label: 'Puja' },
            { key: 'devanagari', label: 'नाम' },
            { key: 'category', label: 'Category' },
            { key: 'status', label: 'Status' },
          ]}
          rows={rows.map((p) => ({
            id: p.id,
            name: p.name_en,
            /* The Devanagari is its own column, never a translation of the first. */
            devanagari: p.name_hi,
            category: categoryName(p.puja_category_id),
            status: (
              <StatusChip
                tone={p.deleted_at ? 'error' : p.is_active ? 'success' : 'neutral'}
                label={p.deleted_at ? 'archived' : p.is_active ? 'active' : 'inactive'}
              />
            ),
          }))}
          focusedIndex={focus}
          onFocusRow={setFocus}
          onRowClick={(r) => setOpen(rows.find((p) => p.id === r.id)!)}
          footer={`${count(pujas.data!.total, 'puja')} · ${QUEUE_HINT}`}
        />
      )}
    </>
  );
}
